import { Icon, Portal } from '@chakra-ui/react';
import { Maybe } from '@monetize/types/common';
import { formatInteger } from '@monetize/utils/core';
import { useEffect, useMemo, useState } from 'react';
import { MdClose } from 'react-icons/md';
import {
  MBox,
  MButton,
  MCheckbox,
  MDivider,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MFlex,
} from '../../../../components/Monetize';
import MSearchInput from '../../../../components/Monetize/MSearchInput';
import MText from '../../../../components/Monetize/MText';
import { PRODUCT_TYPE_DISPLAY } from '../../../../constants/products';
import { isOneTimeInvoiceProductSelected } from '../oneTimeInvoice.utils';

export interface ProductItem {
  fieldId: string;
  productId: string;
  name: string;
  description?: Maybe<string>;
  productType: string;
  isMandatory: boolean;
  isSelected?: boolean | null;
}

interface OneTimeInvoiceManageProductsDrawerProps {
  isOpen: boolean;
  offeringName: string;
  products: ProductItem[];
  onClose: () => void;
  onSelectProduct: (productIndex: number, newVal: boolean) => void;
}

export const OneTimeInvoiceManageProductsDrawer = ({
  isOpen,
  offeringName,
  products,
  onClose,
  onSelectProduct,
}: OneTimeInvoiceManageProductsDrawerProps) => {
  const [drawerProducts, setDrawerProducts] = useState<ProductItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setDrawerProducts(products);
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) {
      return drawerProducts;
    }
    return drawerProducts.filter(({ name }) =>
      name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [drawerProducts, searchTerm]);

  const allSelected = useMemo(() => {
    if (filteredProducts.length === 0) {
      return false;
    }
    return filteredProducts.every(isOneTimeInvoiceProductSelected);
  }, [filteredProducts]);

  const someSelected = useMemo(
    () => filteredProducts.some(isOneTimeInvoiceProductSelected),
    [filteredProducts],
  );

  const hasChanges = useMemo(() => {
    if (drawerProducts.length !== products.length) {
      return true;
    }
    return drawerProducts.some(
      (drawerProduct, idx) =>
        isOneTimeInvoiceProductSelected(drawerProduct) !==
        isOneTimeInvoiceProductSelected(products[idx]),
    );
  }, [drawerProducts, products]);

  /** Mandatory products stay selected regardless of the toggle. */
  const handleSelectAllChange = () => {
    const newValue = !allSelected;
    const filteredFieldIds = new Set(
      filteredProducts.map(({ fieldId }) => fieldId),
    );

    setDrawerProducts((prev) =>
      prev.map((product) =>
        filteredFieldIds.has(product.fieldId)
          ? { ...product, isSelected: product.isMandatory || newValue }
          : product,
      ),
    );
  };

  const handleToggleProduct = (fieldId: string, checked: boolean) => {
    setDrawerProducts((prev) =>
      prev.map((product) =>
        product.fieldId === fieldId
          ? { ...product, isSelected: product.isMandatory || checked }
          : product,
      ),
    );
  };

  const handleSave = () => {
    drawerProducts.forEach((drawerProduct, idx) => {
      const isSelected = isOneTimeInvoiceProductSelected(drawerProduct);

      if (isSelected !== isOneTimeInvoiceProductSelected(products[idx])) {
        onSelectProduct(idx, isSelected);
      }
    });

    onClose();
  };

  const selectedCount = drawerProducts.filter(
    isOneTimeInvoiceProductSelected,
  ).length;
  const totalCount = drawerProducts.length;

  return (
    <MDrawer
      placement="end"
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
      size="md"
      closeOnInteractOutside={false}
    >
      <Portal>
        <MDrawerOverlay />
        <MDrawerPositioner>
          <MDrawerContent>
            <MDrawerCloseButton>
              <Icon asChild>
                <MdClose />
              </Icon>
            </MDrawerCloseButton>
            <MDrawerHeader borderBottomWidth="1px">
              {offeringName}
            </MDrawerHeader>
            <MDrawerBody>
              <MBox mb={4}>
                <MSearchInput
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search products..."
                  count={filteredProducts.length}
                />
              </MBox>

              <MFlex align="center" mb={2}>
                <MCheckbox
                  isChecked={allSelected}
                  isIndeterminate={someSelected && !allSelected}
                  isDisabled={filteredProducts.length === 0}
                  onChange={handleSelectAllChange}
                  mr={3}
                >
                  <MText fontSize="sm">
                    Select All ({formatInteger(selectedCount)}/
                    {formatInteger(totalCount)})
                  </MText>
                </MCheckbox>
              </MFlex>
              <MDivider mb={2} />

              {filteredProducts.map((product) => {
                return (
                  <MFlex key={product.fieldId} align="center" py={4}>
                    <MCheckbox
                      isChecked={isOneTimeInvoiceProductSelected(product)}
                      isDisabled={product.isMandatory}
                      isReadOnly={product.isMandatory}
                      onChange={(e) =>
                        handleToggleProduct(product.fieldId, e.target.checked)
                      }
                      mr={3}
                    >
                      <MText ml={1}>
                        <MText as="span" fontWeight="bold">
                          {product.name}
                        </MText>{' '}
                        {`(${PRODUCT_TYPE_DISPLAY[product.productType]})`}
                      </MText>
                      <MText ml={1} mt={2} fontSize="sm" color="fg.muted">
                        {product.description}
                      </MText>
                    </MCheckbox>
                  </MFlex>
                );
              })}
            </MDrawerBody>

            <MDrawerFooter borderTopWidth="1px">
              <MFlex w="full" justifyContent="flex-end">
                <MButton variant="outline" mr={3} onClick={onClose}>
                  Cancel
                </MButton>
                <MButton
                  variant="primary"
                  onClick={handleSave}
                  isDisabled={!hasChanges}
                >
                  Save
                </MButton>
              </MFlex>
            </MDrawerFooter>
          </MDrawerContent>
        </MDrawerPositioner>
      </Portal>
    </MDrawer>
  );
};
